import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertTriangle,
  Building2,
  Calendar,
  Users,
  Wrench,
  CreditCard,
} from "lucide-react";
import { toast } from "sonner";

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0">
      <Icon className="h-4 w-4 mt-1 text-slate-400" />
      <div className="flex-1">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
          {label}
        </p>
        <p className="text-sm text-slate-900 mt-0.5">{value}</p>
      </div>
    </div>
  );
}

function DeleteAccountSection({ user }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const isManager = user?.role === "manager";
  const target = isManager
    ? "DELETE COMPANY"
    : "DELETE MY LOGIN";
  const ready = confirm.trim().toUpperCase() === target;

  const onDelete = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      await api.delete("/account/me");
      toast.success("Account deleted");
      await logout();
      navigate("/signup", { replace: true });
    } catch (err) {
      console.error("Delete account failed:", err);
      toast.error("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 border-red-200 bg-red-50/30" data-testid="account-delete-section">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-red-100 text-red-700 flex items-center justify-center">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-700">
            GDPR — Right to erasure
          </p>
          <h2 className="font-display text-xl font-bold text-slate-900 mt-1">
            Delete {isManager ? "your account and all data" : "your employee login"}
          </h2>
          <p className="text-sm text-slate-700 mt-2 leading-relaxed">
            {isManager ? (
              <>
                This permanently removes your company, every employee, every
                tool, every exposure record, every check-in and every user
                login. <strong>This cannot be undone.</strong> Download your
                PDF / CSV reports first if you need them.
              </>
            ) : (
              <>
                This removes only your employee login credentials. The exposure
                records associated with the employees your company has logged
                will remain (they belong to the company).
              </>
            )}
          </p>

          <div className="mt-5">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
              Type{" "}
              <span className="font-mono text-red-700">{target}</span> to
              confirm
            </Label>
            <Input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={target}
              className="mt-2 max-w-sm border-red-200"
              data-testid="account-delete-confirm-input"
            />
          </div>

          <Button
            onClick={onDelete}
            disabled={!ready || busy}
            className="mt-5 bg-red-600 hover:bg-red-700 text-white"
            data-testid="account-delete-confirm-btn"
          >
            {busy ? "Deleting…" : `Permanently delete ${isManager ? "company" : "login"}`}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function Account() {
  const { user } = useAuth();
  const fetcher = useCallback(
    () => api.get("/account/status").then((r) => r.data),
    []
  );
  const { data, loading } = useFetch(fetcher);

  if (loading) return <p className="text-slate-500">Loading…</p>;
  if (!data) return null;

  const { company, plan, trial_days_remaining, trial_ends_at, active } = data;
  const trialBadge =
    company.subscription_status === "active"
      ? "Active subscription"
      : company.subscription_status === "expired" || !active
      ? "Trial expired"
      : `Trial — ${trial_days_remaining} days left`;

  return (
    <div className="space-y-8" data-testid="account-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          Settings
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mt-1">
          Account
        </h1>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-slate-200">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            Company
          </p>
          <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">
            {company.name}
          </h2>
          <span
            className={`mt-3 inline-block text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
              active
                ? "bg-emerald-100 text-emerald-700"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            {trialBadge}
          </span>

          <div className="mt-5">
            <InfoRow
              icon={Calendar}
              label="Trial ends"
              value={
                trial_ends_at
                  ? new Date(trial_ends_at).toLocaleString()
                  : "—"
              }
            />
            <InfoRow icon={Building2} label="Mobile Company ID" value={company.slug || company.id} />
            <InfoRow icon={Building2} label="Internal Company ID" value={company.id} />
            <InfoRow
              icon={CreditCard}
              label="Plan"
              value={`${plan.name} · £${plan.price_gbp} / year`}
            />
            <InfoRow
              icon={Users}
              label="Plan limit"
              value={`${plan.max_managers} manager · ${plan.max_workers} workers`}
            />
            <InfoRow icon={Wrench} label="Signed in as" value={`${user?.name} (${user?.role})`} />
          </div>

          {company.subscription_status !== "active" && (
            <a
              href={`mailto:sales@havsguard.com?subject=${encodeURIComponent(
                `HAVS Guard NT subscription — ${company.name}`
              )}`}
              className="mt-5 inline-block"
            >
              <Button
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="account-contact-sales-btn"
              >
                Contact sales to subscribe
              </Button>
            </a>
          )}
        </Card>

        <DeleteAccountSection user={user} />
      </div>
    </div>
  );
}
